import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/shared/ui/Button';
import { Segmented } from '@/shared/ui/Segmented';
import { cn } from '@/shared/utils/cn';

import { buildEmailDocument, type InlineImages, linkHref, linkifyText } from './emailHtml';

type View = 'original' | 'text';

/**
 * The body of an email as the mailbox shows it.
 *
 * With HTML, the HTML wins: that is what the sender designed and what the mail client
 * shows. «Texto» switches to the plain version when the email brings one. Remote images
 * stay off until someone presses «Mostrar imágenes» (what the pixel of a marketing email
 * is after is exactly that the mail was opened), and the choice lasts while the screen
 * is open, like a mail client asks again for the next email.
 *
 * The `iframe` is `sandbox` without `allow-scripts`: nothing in the email runs. It does
 * carry `allow-same-origin`, which on its own only lets **this** page read the frame's
 * height to grow it to the content; the combination that breaks a sandbox is
 * `allow-scripts` plus `allow-same-origin`, and the first one is never granted. Links
 * open in a new tab outside the sandbox (`allow-popups-to-escape-sandbox`) with
 * `noopener noreferrer`, so the page they open knows neither this app nor who opened it.
 */
export function EmailBody({
  text,
  html,
  inlineImages = null,
}: {
  text: string | null;
  html: string | null;
  /** The images the email carries inside itself (`cid:`), served by the API. */
  inlineImages?: InlineImages | null;
}) {
  const [view, setView] = useState<View>('original');
  const [remoteContent, setRemoteContent] = useState(false);

  const shownHtml = html && (view === 'original' || !text) ? html : null;
  const emailDoc = useMemo(
    () => (shownHtml ? buildEmailDocument(shownHtml, { remoteContent, inlineImages }) : null),
    [shownHtml, remoteContent, inlineImages],
  );

  if (!html && !text) {
    return <p className="text-ink-3 p-5 text-sm">Este correo no tiene cuerpo.</p>;
  }

  const askForImages = emailDoc?.hasRemoteContent === true && !remoteContent;
  const canSwitch = Boolean(html && text);
  return (
    <div className="flex flex-col">
      {askForImages || canSwitch ? (
        <div className="border-line flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2">
          {askForImages ? (
            <p className="text-ink-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
              <span>Imágenes ocultas para que el remitente no sepa que has abierto el correo.</span>
              <Button size="sm" variant="secondary" onClick={() => setRemoteContent(true)}>
                Mostrar imágenes
              </Button>
            </p>
          ) : null}
          {canSwitch ? (
            <Segmented
              label="Cómo ver el correo"
              value={view}
              onChange={setView}
              options={[
                { value: 'original', label: 'Original' },
                { value: 'text', label: 'Texto' },
              ]}
              className="ml-auto"
            />
          ) : null}
        </div>
      ) : null}
      {emailDoc ? (
        <HtmlBody srcDoc={emailDoc.html} rounded={!askForImages && !canSwitch} />
      ) : (
        <TextBody text={text ?? ''} />
      )}
    </div>
  );
}

function TextBody({ text }: { text: string }) {
  const parts = useMemo(() => linkifyText(text), [text]);
  return (
    <pre className="text-ink overflow-x-auto p-5 font-sans text-[14.5px] leading-relaxed whitespace-pre-wrap">
      {parts.map((part, index) =>
        part.kind === 'link' ? (
          <a
            key={index}
            href={linkHref(part.value)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent break-all underline underline-offset-2"
          >
            {part.value}
          </a>
        ) : (
          part.value
        ),
      )}
    </pre>
  );
}

/** Before the frame has been measured, and when it cannot be (tests, an odd browser). */
const FALLBACK_HEIGHT = 'h-[32rem]';

/**
 * Below this, a wide newsletter is not shrunk any further: it keeps its horizontal
 * scroll instead of turning into unreadable print.
 */
const MIN_ZOOM = 0.55;

function HtmlBody({ srcDoc, rounded }: { srcDoc: string; rounded: boolean }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [measured, setMeasured] = useState(false);

  /**
   * Fits the email to the frame, like a phone's mail app: a newsletter laid out at 600 px
   * is scaled down (CSS `zoom` on its body) when the column is narrower, and the frame
   * grows to the content's height so the page scrolls and not a box inside it.
   *
   * The frame goes to zero before reading so the content's own height is what comes back
   * and not the frame's (which would never let it shrink when the window widens); all the
   * writes happen in the same task, so nothing is painted at zero. The vertical scrollbar
   * is hidden for good: the frame is always as tall as its content, and a scrollbar that
   * appears only while measuring would steal width and change the result.
   */
  const fit = useCallback(() => {
    const frame = frameRef.current;
    let doc: Document | null | undefined;
    try {
      doc = frame?.contentDocument;
    } catch {
      doc = null; // The frame navigated somewhere it is not ours to read.
    }
    const root = doc?.documentElement;
    const body = doc?.body;
    if (!frame || !root || !body) return;

    root.style.overflowY = 'hidden';
    body.style.zoom = '';
    frame.style.height = '0px';
    const available = root.clientWidth;
    const natural = root.scrollWidth;
    if (natural > available) {
      body.style.zoom = String(Math.max(available / natural, MIN_ZOOM));
    }
    const scrollbar = root.scrollWidth > root.clientWidth ? 18 : 0;
    frame.style.height = `${Math.max(root.scrollHeight, 80) + scrollbar}px`;
    setMeasured(true);
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === 'undefined') return;
    // Only the width matters: the height is ours, and reacting to it would loop.
    let width = frame.clientWidth;
    const observer = new ResizeObserver(() => {
      if (frame.clientWidth === width) return;
      width = frame.clientWidth;
      fit();
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [fit]);

  return (
    <iframe
      ref={frameRef}
      title="Cuerpo del correo"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      onLoad={fit}
      className={cn(
        'block w-full bg-white',
        rounded ? 'rounded-[10px]' : 'rounded-b-[10px]',
        !measured && FALLBACK_HEIGHT,
      )}
    />
  );
}
